/**
 * shared/rbac — políticas de autorização do cliente (defense-in-depth).
 *
 * Espelha a Lista Canônica 4.1 (rotas) e as flags de contexto (`isMaster`,
 * `canSwitchBU`). É APENAS defesa em profundidade: esconde UI que o usuário
 * não pode ver/fazer, mas a autorização final é SEMPRE do servidor (RNF 2,
 * ADR-0001). O portal trata 401/403 como verdade canônica.
 *
 * Funções puras: este módulo não importa React.
 *
 * MIGRAÇÃO PARCIAL (TASK-23, change `permission-based-access`, G6.json): os pontos de decisão
 * inventariados em `docs/product/authz/inventory/G6.json` foram trocados de RBAC-por-papel para
 * `canDo` do modelo de permissões (`shared/authz/canDo.ts`, TASK-15) — ver `ROUTE_PERMISSION_KEY`
 * e `ACTION_PERMISSION_KEY` abaixo. Os demais pontos deste arquivo (rotas `partners`, `admin`,
 * `platform`, `queues`, e a ação `create:opportunity`) NÃO estão no `G6.json` — permanecem no
 * mecanismo RBAC legado (`ROUTE_ROLES`/`ACTION_ROLES`/`ROUTE_EXCLUDED_ROLES`/`isMaster`),
 * fora do escopo desta task (candidatos a uma curadoria futura do inventário).
 */

import type { UserRole } from "../../types/domain.js";
import { FEATURE_GATED_ROUTES, isFeatureAvailable } from "../features/featureAvailability.js";
import { canDo as canDoPermission } from "../authz/canDo.js";

/**
 * Garantia em tempo de compilação de que todo id em `FEATURE_GATED_ROUTES` é uma
 * rota conhecida. Sem isto, um erro de digitação lá viraria um portão que nunca
 * fecha, silenciosamente. A checagem mora aqui (e não no outro módulo) para que
 * `featureAvailability` continue sem depender do RBAC.
 */
const _gatedRoutesAreKnown: readonly RouteId[] = FEATURE_GATED_ROUTES;
void _gatedRoutesAreKnown;

/** Rotas da Lista Canônica 4.1 do requirements + Wave 2 (channels) + Wave 3 (queues) + Wave 6 (workflows) + revisão de gravação. */
export type RouteId =
  | "dashboard"
  | "pipeline"
  | "opportunity"
  | "partners"
  | "accounts"
  | "account"
  | "activities"
  | "inbox"
  | "conversations"
  | "channels"
  | "reports"
  | "settings"
  | "admin"
  | "platform"
  | "import"
  | "audit"
  | "digest"
  | "queues"
  | "workflows"
  | "recording-review";

/**
 * Contexto de RBAC derivado do TenantContextRef (4.3 do design).
 * `isMaster` é derivado de `role === 'platform_operator'` (ADR-0015).
 */
export interface RbacContext {
  readonly roles: readonly UserRole[];
  readonly isMaster: boolean;
  readonly canSwitchBU: boolean;
}

/**
 * Conjunto de papéis que enxergam cada rota; `null` = qualquer autenticado.
 *
 * TASK-23: `reports`, `channels`, `audit`, `digest`, `workflows` e `import` estão SUPERSEDIDAS
 * por `ROUTE_PERMISSION_KEY` (abaixo) — `canSeeRoute` não consulta mais estas seis entradas
 * (mantidas aqui só como registro histórico do que o mecanismo RBAC decidia antes da migração;
 * `docs/product/authz/inventory/G6.json` é a medição formal desse "antes"). As demais rotas
 * (`partners`, `admin`, `platform`, `queues`) continuam decididas por este mapa — fora do
 * escopo de `G6.json`.
 */
export const ROUTE_ROLES: Record<RouteId, readonly UserRole[] | null> = {
  dashboard: null,
  pipeline: null,
  opportunity: null,
  accounts: null,
  account: null,
  activities: null,
  inbox: null,
  conversations: null,
  settings: null,
  partners: ["bu_manager", "tenant_admin", "platform_operator"],
  // SUPERSEDIDA (TASK-23) — ver ROUTE_PERMISSION_KEY.reports ("report.view").
  reports: ["bu_manager", "tenant_admin", "platform_operator"],
  // SUPERSEDIDA (TASK-23) — ver ROUTE_PERMISSION_KEY.channels ("channel_connection.manage").
  channels: ["tenant_admin", "platform_operator"],
  admin: ["tenant_admin", "platform_operator"],
  platform: ["tenant_admin", "platform_operator"],
  // SUPERSEDIDA (TASK-23) — ver ROUTE_PERMISSION_KEY.import ("migration.execute").
  import: ["tenant_admin", "platform_operator"],
  // SUPERSEDIDA (TASK-23) — ver ROUTE_PERMISSION_KEY.audit ("audit.view").
  audit: ["tenant_admin", "bu_manager", "platform_operator"],
  // SUPERSEDIDA (TASK-23) — ver ROUTE_PERMISSION_KEY.digest ("digest.manage").
  digest: ["tenant_admin", "platform_operator"],
  queues: ["tenant_admin", "platform_operator", "bu_manager"],
  /** SUPERSEDIDA (TASK-23) — ver ROUTE_PERMISSION_KEY.workflows ("workflow.view"). Antes da
   *  migração: gestor_bu (BUs do escopo), tenant_admin; viewer só leitura; platform_operator
   *  bloqueado (RNF 6, via ROUTE_EXCLUDED_ROLES). */
  workflows: ["bu_manager", "tenant_admin", "viewer"],
  /** recording-review: acessível a qualquer usuário autenticado (descoberta via inbox). */
  "recording-review": null,
};

/**
 * Rotas cujo ponto de decisão já foi migrado do RBAC-por-papel para o modelo de permissões
 * (TASK-23, change `permission-based-access`, pontos do `docs/product/authz/inventory/G6.json`).
 * Mapeia `RouteId` → chave de permissão de `contracts/authz/v1/permission-catalog.json`. Rotas
 * ausentes deste mapa continuam decididas pelo mecanismo RBAC legado (`ROUTE_ROLES` +
 * `ROUTE_EXCLUDED_ROLES` + `isMaster`) — fora do escopo de `G6.json`.
 */
const ROUTE_PERMISSION_KEY: Partial<Record<RouteId, string>> = {
  reports: "report.view",
  channels: "channel_connection.manage",
  audit: "audit.view",
  digest: "digest.manage",
  workflows: "workflow.view",
  import: "migration.execute",
};

/**
 * Ações granulares (`canDo`) ainda decididas pelo mecanismo RBAC legado. Deny-by-default para
 * ações não mapeadas. `view:contacts:pii`, `delete:contact`, `create:account` e
 * `write:bu-membership` foram migradas para `ACTION_PERMISSION_KEY` (TASK-23, `G6.json`) —
 * removidas daqui porque `canDo` não as consulta mais.
 */
const ACTION_ROLES: Record<string, readonly UserRole[]> = {
  // Criação de oportunidade — papéis comerciais e administradores (Finding F10, Req 7.4).
  // viewer é somente leitura: não cria oportunidades. Fora do escopo de G6.json (TASK-23):
  // G6.json inventariou "create:account" mas não "create:opportunity" apesar da mesma política —
  // achado fora do escopo desta task, reportado no relatório final.
  "create:opportunity": [
    "sales_ae",
    "sales_sdr",
    "sales_se",
    "bu_manager",
    "tenant_admin",
    "platform_operator",
  ],
};

/**
 * Ações cujo ponto de decisão já foi migrado do RBAC-por-papel para o modelo de permissões
 * (TASK-23, pontos do `docs/product/authz/inventory/G6.json`, exceto `delete:account` — ver
 * nota abaixo). Mapeia id de ação (`canDo`) → chave de permissão do catálogo. Sem atalho de
 * `isMaster`: o alcance de `platform_operator` já vem do perfil padrão (ex.: `account.view_pii`
 * e `account.forget_contact` = `none` para `platform_operator`, `docs/product/authz/
 * baseline-deltas.md`).
 */
const ACTION_PERMISSION_KEY: Partial<Record<string, string>> = {
  // PII de contatos (Req 13.2) — `own`/`unit`/`org` conforme `default-profiles.json`.
  "view:contacts:pii": "account.view_pii",
  // Anonimização LGPD de contato (Req 13.3) — só `tenant_admin` (`org`).
  "delete:contact": "account.forget_contact",
  // Criação de conta (Finding F10, Req 7.4) — `org` para todo papel (viewer incluído: a
  // divergência anterior era restritiva e está aprovada em baseline-deltas.md).
  "create:account": "account.create",
  // Escrita de vínculo (assign/change/remove membership) no MembersPanel do console de
  // Plataforma (REQ-18). D-25 opção b restringia a `platform_operator`; a migração para
  // `bu_membership.write` PASSA A CONCEDER também a `tenant_admin` (`org` no catálogo), que é
  // quem o `organization` de fato exige nos três comandos de membership (G1.json) — divergência
  // aprovada em `baseline-deltas.md` (`bu_membership.write|tenant_admin|
  // azim-portal.action-write-bu-membership`), corrige o gap restritivo descrito em G6.json sem
  // decisão nova desta task.
  "write:bu-membership": "bu_membership.write",
  // Exclusão de conta (Req 12) — achado fora de G6.json (não inventariado), migrado nesta task
  // por ser um ponto de decisão do mesmo arquivo escopo (`pages/accounts/AccountsPage.tsx`) com
  // paridade exata ao alcance já em produção (`account.delete` = `org` só para `tenant_admin`,
  // `none` para os demais, incluindo `platform_operator` — sem divergência com o medido). Resolve
  // o comentário histórico do arquivo sobre `canDo()` não servir por causa do atalho de
  // `isMaster`: a partir desta task, `canDo` para ações migradas não tem mais esse atalho.
  "delete:account": "account.delete",
  // Escrita/leitura de automações (Req workflows, design.md §10) — achado fora de G6.json,
  // migrado em `features/workflows/hooks.ts` (useWorkflowRbac) com paridade ao já decidido para
  // a ROTA `workflows` acima (mesma permissão `workflow.view`/`workflow.manage`).
  "write:workflow": "workflow.manage",
  "view:workflow": "workflow.view",
};

/**
 * Rotas com exclusão explícita de papéis (incluindo master/platform_operator).
 *
 * Quando uma rota declara exclusão de um papel, essa exclusão prevalece sobre
 * o short-circuit de `isMaster` (SEC-001). Isso permite bloquear platform_operator
 * de áreas de dados comerciais de tenant mesmo sendo master técnico (RNF 6).
 *
 * Uso: adicionar a rota aqui quando a spec disser explicitamente que um papel
 * é bloqueado independente de ser master (ex.: "platform_operator NÃO acessa workflows").
 */
const ROUTE_EXCLUDED_ROLES: Partial<Record<RouteId, readonly UserRole[]>> = {
  // (vazio) `workflows` era a única entrada (platform_operator, RNF 6) — SUPERSEDIDA pela
  // migração para `workflow.view` (TASK-23, ROUTE_PERMISSION_KEY): o alcance `none` de
  // platform_operator já vem do perfil padrão, sem precisar de exclusão explícita nem de
  // short-circuit de isMaster (rotas migradas não consultam nenhum dos dois — ver canSeeRoute).
};

function hasAnyRole(ctx: RbacContext, allowed: readonly UserRole[]): boolean {
  return ctx.roles.some((role) => allowed.includes(role));
}

function isExplicitlyExcluded(route: RouteId, ctx: RbacContext): boolean {
  const excluded = ROUTE_EXCLUDED_ROLES[route];
  if (!excluded) return false;
  return ctx.roles.some((role) => excluded.includes(role));
}

/**
 * Contexto de permissão usado pelas rotas/ações migradas (TASK-23) ao chamar `canDoPermission`.
 * `own: true, unit: true` é deliberadamente permissivo: o GUARD só precisa saber se o papel tem
 * QUALQUER alcance não-"none" para a permissão (a granularidade real — own/unit/org — é aplicada
 * pela página/consulta que carrega os dados, nunca pelo guard de rota/ação em si). Ver design
 * §2.6 e os casos `azim-portal.route-reports`/`action-view-contacts-pii` em
 * `__characterization__/authz-cases.json` (sales_ae vê /reports com alcance "own", por exemplo).
 */
const PERMISSIVE_CONTEXT = { own: true, unit: true } as const;

/**
 * Decide se a rota deve ser visível para o contexto.
 *
 * Rotas migradas (TASK-23, `ROUTE_PERMISSION_KEY`) decidem PELO CATÁLOGO DE PERMISSÕES —
 * `isFeatureAvailable` ainda se aplica (funcionalidade indisponível é sempre invisível,
 * inclusive migrada), mas NEM `isExplicitlyExcluded` NEM o short-circuit de `isMaster` são
 * consultados: o alcance de cada papel, inclusive `platform_operator`, já vem resolvido do
 * perfil padrão (`contracts/authz/v1/default-profiles.json` via `canDo` de `shared/authz/`).
 *
 * Rotas NÃO migradas (fora do escopo de `G6.json`) seguem a ordem de avaliação legada (SEC-001):
 * 0. Se a funcionalidade não está disponível no ambiente → false (antes de tudo,
 *    inclusive de `isMaster`: uma tela inacabada não existe para ninguém).
 * 1. Se o papel está EXPLICITAMENTE EXCLUÍDO da rota → false (prevalece sobre isMaster).
 * 2. Se `isMaster` → true (master vê tudo exceto rotas com exclusão explícita).
 * 3. Rota com `null` em `ROUTE_ROLES` → true (qualquer autenticado).
 * 4. `hasAnyRole` verifica se o papel está na allowlist da rota.
 */
export function canSeeRoute(route: RouteId, ctx: RbacContext): boolean {
  // Funcionalidade desligada no ambiente: invisível para todo papel, inclusive master ou
  // qualquer papel com alcance no catálogo de permissões.
  if (!isFeatureAvailable(route)) return false;

  const permissionKey = ROUTE_PERMISSION_KEY[route];
  if (permissionKey !== undefined) {
    return ctx.roles.some((role) => canDoPermission(role, permissionKey, PERMISSIVE_CONTEXT));
  }

  // Mecanismo RBAC legado — rota fora do escopo de G6.json (TASK-23).
  // SEC-001: exclusão explícita tem precedência sobre isMaster
  if (isExplicitlyExcluded(route, ctx)) return false;
  if (ctx.isMaster) return true;
  const allowed = ROUTE_ROLES[route];
  if (allowed === null) return true;
  return hasAnyRole(ctx, allowed);
}

/**
 * Decide se a ação é permitida para o contexto.
 *
 * Ações migradas (TASK-23, `ACTION_PERMISSION_KEY`) decidem pelo catálogo de permissões, sem o
 * atalho de `isMaster` (o mesmo raciocínio de `canSeeRoute` acima). Ações NÃO migradas seguem o
 * mecanismo RBAC legado: `isMaster` pode tudo, ação desconhecida é negada (deny-by-default).
 */
export function canDo(action: string, ctx: RbacContext): boolean {
  const permissionKey = ACTION_PERMISSION_KEY[action];
  if (permissionKey !== undefined) {
    return ctx.roles.some((role) => canDoPermission(role, permissionKey, PERMISSIVE_CONTEXT));
  }

  if (ctx.isMaster) return true;
  const allowed = ACTION_ROLES[action];
  if (!allowed) return false;
  return hasAnyRole(ctx, allowed);
}
